// The administration API (spec §9), typed end to end. Every call goes through the one wrapper in
// api.ts, so the CSRF header, the envelope and the 401 redirect are never re-implemented here.
// Nothing in this module decides access: a 403 comes back as a failure the page renders as the
// permission-denied state (03-components §1), never as an empty list.
import type {
  AdminGroupDetail,
  AdminGroupsResponse,
  AdminUserDetail,
  AdminUsersResponse,
  AuditListResponse,
  CostsBreakdownResponse,
  CostsByDayResponse,
  CostsCallsResponse,
  CostsSummaryResponse,
  CreateGroupRequest,
  DeleteGroupResponse,
  DeletedResponse,
  GroupAuditResponse,
  QueueResponse,
  PutGroupGrantsRequest,
  SuperuserAuditResponse,
  UpdateGroupRequest,
} from '../../shared/api-types.js';
import { api, queryString, type ApiResult } from '../api.js';

const enc = encodeURIComponent;

export const adminApi = {
  // --- Users (06-modules §1) --------------------------------------------------------------------
  users: (signal?: AbortSignal): Promise<ApiResult<AdminUsersResponse>> => api.get<AdminUsersResponse>('/api/admin/users', { signal }),
  user: (id: string, signal?: AbortSignal): Promise<ApiResult<AdminUserDetail>> => api.get<AdminUserDetail>(`/api/admin/users/${enc(id)}`, { signal }),
  /** Approves a pending account, or reinstates a denied/suspended one; at least one group is required. */
  approve: (id: string, groupIds: readonly string[]): Promise<ApiResult<AdminUserDetail>> =>
    api.post<AdminUserDetail>(`/api/admin/users/${enc(id)}/approve`, { groupIds }),
  deny: (id: string): Promise<ApiResult<AdminUserDetail>> => api.post<AdminUserDetail>(`/api/admin/users/${enc(id)}/deny`, {}),
  suspend: (id: string): Promise<ApiResult<AdminUserDetail>> => api.post<AdminUserDetail>(`/api/admin/users/${enc(id)}/suspend`, {}),
  revokeSessions: (id: string): Promise<ApiResult<AdminUserDetail>> => api.post<AdminUserDetail>(`/api/admin/users/${enc(id)}/revoke-sessions`, {}),
  setUserGroups: (id: string, groupIds: readonly string[]): Promise<ApiResult<AdminUserDetail>> =>
    api.put<AdminUserDetail>(`/api/admin/users/${enc(id)}/groups`, { groupIds }),
  deleteUser: (id: string): Promise<ApiResult<DeletedResponse>> => api.delete<DeletedResponse>(`/api/admin/users/${enc(id)}`),

  // --- Groups (06-modules §2) -------------------------------------------------------------------
  groups: (signal?: AbortSignal): Promise<ApiResult<AdminGroupsResponse>> => api.get<AdminGroupsResponse>('/api/admin/groups', { signal }),
  group: (id: string, signal?: AbortSignal): Promise<ApiResult<AdminGroupDetail>> => api.get<AdminGroupDetail>(`/api/admin/groups/${enc(id)}`, { signal }),
  createGroup: (body: CreateGroupRequest): Promise<ApiResult<AdminGroupDetail>> => api.post<AdminGroupDetail>('/api/admin/groups', body),
  updateGroup: (id: string, body: UpdateGroupRequest): Promise<ApiResult<AdminGroupDetail>> => api.patch<AdminGroupDetail>(`/api/admin/groups/${enc(id)}`, body),
  /**
   * `expectedMemberCount` is the count the operator was shown; the server refuses (409) if
   * membership changed in between, so a confirmation never deletes access from people unseen.
   */
  deleteGroup: (id: string, expectedMemberCount: number): Promise<ApiResult<DeleteGroupResponse>> =>
    api.delete<DeleteGroupResponse>(`/api/admin/groups/${enc(id)}${queryString({ expectedMemberCount })}`),
  setGrants: (id: string, body: PutGroupGrantsRequest): Promise<ApiResult<AdminGroupDetail>> => api.put<AdminGroupDetail>(`/api/admin/groups/${enc(id)}/grants`, body),
  setMembers: (id: string, subjectIds: readonly string[]): Promise<ApiResult<AdminGroupDetail>> =>
    api.put<AdminGroupDetail>(`/api/admin/groups/${enc(id)}/members`, { subjectIds }),
  groupAudit: (id: string, signal?: AbortSignal): Promise<ApiResult<GroupAuditResponse>> => api.get<GroupAuditResponse>(`/api/admin/groups/${enc(id)}/audit`, { signal }),
  /** The env-derived superuser's grants and revocations, from their durable table; superuser-only, newest first. */
  superuserAudit: (signal?: AbortSignal): Promise<ApiResult<SuperuserAuditResponse>> => api.get<SuperuserAuditResponse>('/api/admin/superuser/audit', { signal }),

  // --- Audit log (06-modules §3) ----------------------------------------------------------------
  audit: (query: Record<string, string | number | boolean | null | undefined>, signal?: AbortSignal): Promise<ApiResult<AuditListResponse>> =>
    api.get<AuditListResponse>(`/api/admin/audit${queryString(query)}`, { signal }),

  // --- Costs: the AI call ledger (owner ruling of 2026-09-27) -----------------------------------
  costsSummary: (query: CostsQuery, signal?: AbortSignal): Promise<ApiResult<CostsSummaryResponse>> =>
    api.get<CostsSummaryResponse>(`/api/admin/costs/summary${queryString(query)}`, { signal }),
  costsByDay: (query: CostsQuery, signal?: AbortSignal): Promise<ApiResult<CostsByDayResponse>> =>
    api.get<CostsByDayResponse>(`/api/admin/costs/by-day${queryString(query)}`, { signal }),
  costsBreakdown: (query: CostsQuery, signal?: AbortSignal): Promise<ApiResult<CostsBreakdownResponse>> =>
    api.get<CostsBreakdownResponse>(`/api/admin/costs/breakdown${queryString(query)}`, { signal }),
  costsCalls: (query: CostsQuery, signal?: AbortSignal): Promise<ApiResult<CostsCallsResponse>> =>
    api.get<CostsCallsResponse>(`/api/admin/costs/calls${queryString(query)}`, { signal }),
  /** The export is a plain link (the server answers with an attachment), so this only builds its address. */
  costsExportUrl: (query: CostsQuery): string => `/api/admin/costs/export.csv${queryString(query)}`,

  // --- Queue: what the worker runs and what waits (owner ruling 7 of 2026-09-28) -----------------
  queue: (signal?: AbortSignal): Promise<ApiResult<QueueResponse>> => api.get<QueueResponse>('/api/admin/queue', { signal }),
};

type CostsQuery = Record<string, string | number | boolean | null | undefined>;
